import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ShieldCheck } from "lucide-react";
import { FactsIntro } from "@/components/FactsIntro";
import { toast } from "sonner";
import { api, browserCountry, errMsg } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { useMe } from "@/context/MeContext";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LANGS, TopicPicker } from "@/pages/Preferences";

export const NEEDS = [["needs_people", Heart, "--social", "Facts from the people you follow."], ["needs_info", ShieldCheck, "--facts", "Facts and Middle Live from approved sources, with evidence."]];

export const NeedsGrid = () => {
  const b = useBrand();
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="four-needs">
      {NEEDS.map(([k, Icon, v, text]) => (
        <div key={k} className="surface flex gap-3 p-4"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ background: `hsl(var(${v}) / 0.1)`, color: `hsl(var(${v}))` }}><Icon size={17} /></span>
          <div><p className="font-serif text-lg leading-tight">{b[k]}</p><p className="text-xs text-slate-500">{text}</p></div></div>
      ))}
    </div>
  );
};

export default function Welcome() {
  const { me, refresh } = useMe();
  const nav = useNavigate();
  const b = useBrand();
  const [step, setStep] = useState(-1);
  const [interests, setInterests] = useState(me.interests || []);
  const [language, setLanguage] = useState(me.language || "en");
  const [loc, setLoc] = useState({ suburb: me.suburb || "", postcode: "", state: "", country: browserCountry(), radius_km: 25 });
  const [busy, setBusy] = useState(false);
  const finish = async () => {
    setBusy(true);
    try {
      if (loc.suburb || loc.country) await api.put("/me/location", loc);
      await api.patch("/me", { interests, language, onboarded: true });
      await refresh(); nav("/");
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="welcome-page">
      <Wordmark />
      {step === -1 && <FactsIntro onDone={() => setStep(0)} />}
      {step === 0 && (<div className="space-y-6 fade-up">
        <h1 className="text-4xl font-medium tracking-tight sm:text-5xl">Welcome to Middle, {me.name?.split(" ")[0]}.</h1>
        <p className="text-slate-600">One account, three things people actually come here for:</p>
        <NeedsGrid />
        <p className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 font-serif text-lg text-emerald-900">{b.facts_campaign}</p>
        <Button data-testid="welcome-next-0" className="rounded-full px-7" onClick={() => setStep(1)}>Get started</Button></div>)}
      {step === 1 && (<div className="space-y-5 fade-up">
        <h1 className="text-3xl font-medium">What are you into?</h1><p className="text-sm text-slate-500">Pick a few. You can change these anytime in Preferences.</p>
        <TopicPicker value={interests} onChange={setInterests} testid="welcome-interest" />
        <div className="flex gap-2"><Button variant="ghost" onClick={() => setStep(0)}>Back</Button><Button data-testid="welcome-next-1" className="rounded-full px-7" onClick={() => setStep(2)}>Next</Button></div></div>)}
      {step === 2 && (<div className="space-y-5 fade-up">
        <h1 className="text-3xl font-medium">Where are you, roughly?</h1><p className="text-sm text-slate-500">So Facts can show local and national news first. Stored to about 1 km and never shown precisely.</p>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="welcome-suburb" placeholder="Suburb" value={loc.suburb} onChange={(e) => setLoc({ ...loc, suburb: e.target.value })} />
          <Input data-testid="welcome-postcode" placeholder="Postcode" value={loc.postcode} onChange={(e) => setLoc({ ...loc, postcode: e.target.value })} />
          <Input data-testid="welcome-state" placeholder="State / region" value={loc.state} onChange={(e) => setLoc({ ...loc, state: e.target.value })} />
          <Input data-testid="welcome-country" placeholder="Country" value={loc.country} onChange={(e) => setLoc({ ...loc, country: e.target.value })} />
          <Select value={language} onValueChange={setLanguage}><SelectTrigger data-testid="welcome-language"><SelectValue /></SelectTrigger><SelectContent>{LANGS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="flex gap-2"><Button variant="ghost" onClick={() => setStep(1)}>Back</Button><Button data-testid="welcome-finish" className="rounded-full px-7" disabled={busy} onClick={finish}>{busy ? "Saving…" : "Finish"}</Button></div></div>)}
    </div>
  );
}
